import type { Weight } from '@iheartjane/types';
import type { SortingState } from '@tanstack/react-table';
import findKey from 'lodash-es/findKey';
import set from 'lodash-es/set';

import type {
  AbbreviatedSpecialV2,
  SpecialRulesV2,
} from '@jane/business-admin/types';
/* eslint-disable-next-line no-restricted-imports */
import type {
  AdditionalProductWeight,
  WeightValueCondition,
} from '@jane/shared/models';

import { ALL_WEIGHTS } from './weights';

export const MAX_MESSAGE_LENGTH = 300;
export const FORM_FIELD_DEFAULT_MARGIN = 24;

export enum SPECIAL_TYPES {
  BulkPricing = 'bulk_pricing',
  Bundle = 'bundle',
  CartTotal = 'cart_total',
  Product = 'product',
  QualifiedGroup = 'qualified_group',
  SpendingThreshold = 'spending_threshold',
}

export const ADDITIONAL_WEIGHTS: (
  | Weight
  | { id: AdditionalProductWeight; label: string; val: string }
)[] = [
  { id: 'half_gram', label: '0.5g', val: 'price_half_gram' },
  { id: 'gram', label: '1g', val: 'price_gram' },
  { id: 'two_gram', label: '2g', val: 'price_two_gram' },
  { id: 'three_gram', label: '3g', val: 'price_three_gram' },
  { id: 'eighth_ounce', label: '3.5g', val: 'price_eighth_ounce' },
  { id: 'four_gram', label: '4g', val: 'price_four_gram' },
  { id: 'five_gram', label: '5g', val: 'price_five_gram' },
  { id: 'quarter_ounce', label: '7g', val: 'price_quarter_ounce' },
  { id: 'eight_gram', label: '8g', val: 'price_eight_gram' },
  { id: 'ten_gram', label: '10g', val: 'price_ten_gram' },
  { id: 'half_ounce', label: '14g', val: 'price_half_ounce' },
  { id: 'ounce', label: '28g', val: 'price_ounce' },
  { id: 'two_ounce', label: '56g', val: 'price_two_ounce' },
  { id: 'two_and_half_ounce', label: '70g', val: 'price_two_and_half_ounce' },
];

export const shouldShowArchiveButton = ({
  isArchived,
  isConnectedToGlobal,
  isEnabled,
  isPosSynced,
}: {
  isArchived?: boolean;
  isConnectedToGlobal: boolean;
  isEnabled?: boolean;
  isPosSynced?: boolean;
}) => {
  if (isArchived || isConnectedToGlobal) {
    return false;
  }
  if (isPosSynced && isEnabled) {
    return false;
  }
  return true;
};

export const getDiscountAmount = (row: AbbreviatedSpecialV2) => {
  let isPercent = false;
  let amount = 0;

  switch (row.discount_type) {
    case 'percent':
      amount = row.discount_percent || 0;
      isPercent = true;
      break;
    case 'dollar_amount':
      amount = row.discount_dollar_amount || 0;
      break;
    case 'target_price':
      amount = row.discount_target_price || 0;
      break;
  }

  if (isPercent) {
    return `${amount}%`;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(amount);
};

export const specialsSortParams = (
  sorting: SortingState,
  statusFilter: string
) => {
  const isSorted = Array.isArray(sorting) && sorting.length > 0;

  if (!isSorted) return {};

  if (statusFilter !== 'archived' && sorting[0]?.id === 'archived_at') {
    return {};
  }

  const sort = sorting[0].id;
  const order = sorting[0].desc ? 'desc' : 'asc';

  return { sort, order };
};

export const buildRules = ({
  excludeBrand = false,
  excludeBrandId = false,
  excludeBulkWeights = false,
  excludeCategory = false,
  excludeProduct = false,
  excludeWeight = false,
  excludeWeightValue = false,
  includeBrand = false,
  includeBrandId = false,
  includeBulkWeights = false,
  includeCategory = false,
  includeLineage = false,
  includePrices = false,
  includeProduct = false,
  includeWeight = false,
  includeWeightValue = false,
}: {
  excludeBrand?: boolean;
  excludeBrandId?: boolean;
  excludeBulkWeights?: boolean | any[];
  excludeCategory?: boolean;
  excludeProduct?: boolean | any;
  excludeWeight?: boolean | any[];
  excludeWeightValue?: boolean | any;
  includeBrand?: boolean | any;
  includeBrandId?: boolean | any;
  includeBulkWeights?: boolean | any[];
  includeCategory?: boolean | any;
  includeLineage?: boolean | any;
  includePrices?: boolean | any;
  includeProduct?: boolean | any;
  includeWeight?: boolean | any[];
  includeWeightValue?: boolean | any[];
}) => {
  const rules: SpecialRulesV2 = {};
  // TODO: remove once we stop persisting brand name conditions
  if (excludeBrand) {
    rules['excludes'] = [
      {
        brands:
          typeof excludeBrand === 'boolean' ? ['Test Brand'] : excludeBrand,
      },
    ];
  }
  if (excludeBrandId) {
    rules['excludes'] = [
      {
        brand_ids: typeof excludeBrandId === 'boolean' ? [420] : excludeBrandId,
      },
    ];
  }
  if (excludeCategory) {
    if (rules['excludes']) {
      rules['excludes'][0]['kinds'] = [{ kind: 'flower' }];
    } else {
      rules['excludes'] = [
        {
          kinds: [{ kind: 'flower' }],
        },
      ];
    }
  }
  if (excludeProduct) {
    const productRule =
      typeof excludeProduct === 'boolean' ? [1] : excludeProduct;
    set(rules, 'excludes.0.product_ids', productRule);
  }
  if (excludeWeight) {
    const weightRule =
      typeof excludeWeight === 'boolean' ? ['half_gram'] : excludeWeight;
    set(rules, 'excludes.0.weights', weightRule);
  }
  if (excludeWeightValue) {
    const weightRule =
      typeof excludeWeightValue === 'boolean'
        ? [{ units: 'g', value: 0.5 }]
        : excludeWeightValue;
    set(rules, 'excludes.0.weight_values', weightRule);
  }
  // TODO: remove once we stop persisting brand name conditions
  if (includeBrand) {
    const brandRule =
      typeof includeBrand === 'boolean' ? ['Test Brand'] : includeBrand;
    set(rules, 'includes.0.brands', brandRule);
  }
  if (includeBrandId) {
    const brandIdRule =
      typeof includeBrandId === 'boolean' ? [420] : includeBrandId;
    set(rules, 'includes.0.brand_ids', brandIdRule);
  }
  if (includeCategory) {
    const categoryRule =
      typeof includeCategory === 'boolean'
        ? [{ kind: 'flower' }]
        : includeCategory;
    const transformedRule = transformRules(categoryRule);

    set(rules, 'includes.0.kinds', transformedRule);
  }
  if (includeLineage) {
    const lineageRule =
      typeof includeLineage === 'boolean' ? ['cbd'] : includeLineage;
    set(rules, 'includes.0.lineages', lineageRule);
  }
  if (includePrices) {
    const priceRule =
      typeof includePrices === 'boolean'
        ? {
            maximum_price: 100,
            minimum_price: 10,
          }
        : includePrices;
    rules['product_threshold'] = priceRule;
  }
  if (includeProduct) {
    const productRule =
      typeof includeProduct === 'boolean' ? [1] : includeProduct;
    set(rules, 'includes.0.product_ids', productRule);
  }
  if (includeWeight) {
    const weightRule =
      typeof includeWeight === 'boolean' ? ['half_gram'] : includeWeight;
    set(rules, 'includes.0.weights', weightRule);
  }
  if (includeBulkWeights) {
    const weightRule =
      typeof includeBulkWeights === 'boolean'
        ? [
            {
              price_id: 'half_gram',
              maximum_price: 20,
              minimum_price: 10,
            },
          ]
        : includeBulkWeights;
    set(rules, 'includes.0.bulk_weights', weightRule);
  }
  if (excludeBulkWeights) {
    const weightRule =
      typeof excludeBulkWeights === 'boolean'
        ? [{ price_id: 'half_gram' }]
        : excludeBulkWeights;
    set(rules, 'excludes.0.bulk_weights', weightRule);
  }
  if (includeWeightValue) {
    const weightRule =
      typeof includeWeightValue === 'boolean'
        ? ['half_gram']
        : includeWeightValue;
    set(rules, 'includes.0.weight_values', weightRule);
  }
  return rules;
};

/**
 * Transforms rules by ensuring that if an object has both `kind` and `brand_subtype`,
 * it also includes `root_subtype: "(Subcategory not set)"` if `root_subtype` is missing.
 * @param rules The rules object to transform.
 * @returns A transformed rules object with the missing `root_subtype` added where applicable.
 */
export const transformRules = (rules: any): any => {
  if (Array.isArray(rules)) {
    return rules.map((rule) => {
      if (
        typeof rule === 'object' &&
        rule !== null &&
        rule['kind'] &&
        !rule['root_subtype'] &&
        rule['brand_subtype']
      ) {
        // Add the missing root_subtype here
        return {
          ...rule,
          root_subtype: '(Subcategory not set)',
        };
      }
      return transformRules(rule);
    });
  } else if (typeof rules === 'object' && rules !== null) {
    return Object.entries(rules).reduce((acc, [key, value]) => {
      acc[key] = transformRules(value);
      return acc;
    }, {} as any);
  }

  return rules;
};

/**
 * Recursively removes (i.e. "sanitizes") properties where the value is "(Subcategory not set)".
 * @param rules The object to sanitize.
 * @returns A sanitized object with the unwanted properties removed.
 */
export const sanitizeRules = (rules: any): any => {
  if (Array.isArray(rules)) {
    return rules.map((item) => sanitizeRules(item));
  } else if (typeof rules === 'object' && rules !== null) {
    return Object.entries(rules).reduce((acc, [key, value]) => {
      const sanitizedValue = sanitizeRules(value);
      // Defacto way of removing (Subcategory not set) by NOT adding it to acc
      if (
        sanitizedValue !== '(Subcategory not set)' &&
        sanitizedValue !== null
      ) {
        acc[key] = sanitizedValue;
      }
      return acc;
    }, {} as any);
  }
  return rules;
};

// React hook form doesn't like decimals in field names
export const removeDecimals = (string: any) => string.replace('.', '*');
export const restoreDecimals = (string: any) => string.replace('*', '.');

export const generateDefaultWeightValues = (weightKeys: string[]) => {
  const defaultWeights: { [key: string]: any } = {};
  Object.keys(ALL_WEIGHTS).forEach((key: string) => {
    defaultWeights[removeDecimals(key)] = weightKeys.includes(key);
  });
  return defaultWeights;
};

export const weightConditionsToKeys = (conditions: WeightValueCondition[]) => {
  return conditions.reduce<string[]>((prev, con) => {
    const foundKey = findKey(
      ALL_WEIGHTS,
      (weight) => weight.value == con.value && weight.units == con.units
    );
    if (foundKey) {
      prev.push(foundKey);
    }
    return prev;
  }, []);
};
